import api from './api'
import {
  LicenseRequest,
  PaginatedResponse,
  ApiResponse,
  RequestType,
  RequestStatus,
  WorkCenterType,
  ValidationResult,
} from '../types'

export interface RequestFilters {
  employeeId?: string
  requestType?: RequestType
  status?: RequestStatus
  workCenter?: WorkCenterType
  startDateFrom?: string
  startDateTo?: string
  search?: string
  page?: number
  limit?: number
}

export type CompensationType = 'RECUPERABLE' | 'DESCUENTO_NOMINA'

export interface CreateRequestData {
  employeeId: string
  requestType: RequestType
  startDate: string
  endDate: string
  secondDate?: string        // Segunda fecha para DLE con días no consecutivos
  isExtension?: boolean
  parentRequestId?: string
  notes?: string
  attachmentUrl?: string     // URL o base64 del documento adjunto
  attachmentName?: string    // Nombre del archivo adjunto
  compensationType?: CompensationType // Solo para DLE: RECUPERABLE o DESCUENTO_NOMINA
  adminOverride?: boolean             // Admin fuerza aprobación aunque no cumpla reglas
}

export interface UpdateRequestData {
  startDate?: string
  endDate?: string
  secondDate?: string | null  // Segunda fecha para DLE con días no consecutivos
  notes?: string
  attachmentUrl?: string      // URL o base64 del documento adjunto
  attachmentName?: string     // Nombre del archivo adjunto
}

export const requestService = {
  // Verificar si las solicitudes están habilitadas (endpoint público, sin auth)
  async getRequestsStatus(): Promise<{ requestsEnabled: boolean }> {
    const response = await api.get<ApiResponse<{ requestsEnabled: boolean }>>('/requests/status')
    return response.data.data
  },

  async getAll(filters?: RequestFilters): Promise<PaginatedResponse<LicenseRequest>> {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value))
        }
      })
    }
    const response = await api.get<PaginatedResponse<LicenseRequest>>(
      `/requests?${params.toString()}`
    )
    return response.data
  },

  async getById(id: string): Promise<LicenseRequest> {
    const response = await api.get<ApiResponse<LicenseRequest>>(`/requests/${id}`)
    return response.data.data
  },

  async getByEmployee(employeeId: string): Promise<LicenseRequest[]> {
    const response = await api.get<ApiResponse<LicenseRequest[]>>(
      `/requests/employee/${employeeId}`
    )
    return response.data.data
  },

  async validate(data: CreateRequestData): Promise<ValidationResult> {
    const response = await api.post<ApiResponse<ValidationResult>>(
      '/requests/validate',
      data
    )
    return response.data.data
  },

  async create(
    data: CreateRequestData
  ): Promise<{ request: LicenseRequest; validation: ValidationResult }> {
    const response = await api.post<
      ApiResponse<{ request: LicenseRequest; validation: ValidationResult }>
    >('/requests', data)
    return response.data.data
  },

  async update(id: string, data: UpdateRequestData): Promise<LicenseRequest> {
    const response = await api.put<ApiResponse<LicenseRequest>>(
      `/requests/${id}`,
      data
    )
    return response.data.data
  },

  async approve(id: string, notes?: string): Promise<LicenseRequest> {
    const response = await api.post<ApiResponse<LicenseRequest>>(
      `/requests/${id}/approve`,
      { notes }
    )
    return response.data.data
  },

  async deny(id: string, denialReason: string): Promise<LicenseRequest> {
    const response = await api.post<ApiResponse<LicenseRequest>>(
      `/requests/${id}/deny`,
      { denialReason }
    )
    return response.data.data
  },

  async cancel(id: string, cancellationReason?: string): Promise<LicenseRequest> {
    const response = await api.post<ApiResponse<LicenseRequest>>(
      `/requests/${id}/cancel`,
      { cancellationReason }
    )
    return response.data.data
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/requests/${id}`)
  },

  async getStats(options?: { year?: number; employeeId?: string }): Promise<{
    total: number
    byStatus: Record<string, number>
    byType: Record<string, number>
  }> {
    const params = new URLSearchParams()
    if (options?.year) params.append('year', String(options.year))
    if (options?.employeeId) params.append('employeeId', options.employeeId)
    const queryString = params.toString()
    const response = await api.get(`/requests/stats${queryString ? `?${queryString}` : ''}`)
    return response.data.data
  },

  async exportExcel(filters?: RequestFilters): Promise<void> {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page' && key !== 'limit') {
          params.append(key, String(value))
        }
      })
    }
    const response = await api.get(`/requests/export/excel?${params.toString()}`, {
      responseType: 'blob',
    })
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `solicitudes_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  async exportPDF(filters?: RequestFilters): Promise<void> {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && key !== 'page' && key !== 'limit') {
          params.append(key, String(value))
        }
      })
    }
    const response = await api.get(`/requests/export/pdf?${params.toString()}`, {
      responseType: 'blob',
    })
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `solicitudes_${new Date().toISOString().split('T')[0]}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },
}
